import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	getFiles,
	getFile,
	requestUploadUrl,
	confirmUpload,
	getDownloadUrl,
	updateFile,
	deleteFile,
} from '../services/file-api'
import type { FileFilters } from '../services/file-api'
import { uploadToS3, triggerBrowserDownload } from '../utils/upload'

export const useFiles = (filters: FileFilters = {}) =>
	useQuery({
		queryKey: ['files', filters],
		queryFn: () => getFiles(filters),
	})

export const useFile = (fileId: string) =>
	useQuery({
		queryKey: ['files', fileId],
		queryFn: () => getFile(fileId),
		enabled: !!fileId,
	})

export const useUploadFile = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (params: {
			file: File
			entityType?: string
			entityId?: string
			isPublic?: boolean
			description?: string
		}) => {
			// Phase 1: Request upload URL
			const { file: fileRecord, uploadUrl } = await requestUploadUrl({
				fileName: params.file.name,
				contentType: params.file.type,
				entityType: params.entityType,
				entityId: params.entityId,
				isPublic: params.isPublic,
				description: params.description,
			})

			// Phase 2: Upload directly to S3
			await uploadToS3(uploadUrl, params.file)

			// Phase 3: Confirm upload
			const confirmed = await confirmUpload(fileRecord.fileId)
			return confirmed
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['files'] }),
	})
}

export const useDownloadFile = () =>
	useMutation({
		mutationFn: async (fileId: string) => {
			const { downloadUrl, fileName } = await getDownloadUrl(fileId)
			triggerBrowserDownload(downloadUrl, fileName)
			return { downloadUrl, fileName }
		},
	})

export const useUpdateFile = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async ({
			id,
			data,
		}: {
			id: string
			data: { description?: string; entityType?: string; entityId?: string }
		}) => {
			const response = await updateFile(id, data)
			return response
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['files'] }),
	})
}

export const useDeleteFile = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (fileId: string) => {
			await deleteFile(fileId)
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['files'] }),
	})
}
